import { CircleCheck, CircleOff, Pencil, Plus, RefreshCw, Trash2, Undo2, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, CheckboxField, InfoTip, StatusMessage, TextField } from '@krp/ui-kit'

import { PRICE_TYPE_HELP } from '../model/priceTypeHelp'
import type { PriceTypeStateKind, PriceTypesActions, PriceTypesVm } from '../model/priceTypes'
import { QueryState } from './QueryState'
import './onec-ui.css'

const STATE_ICONS: Readonly<Record<PriceTypeStateKind, ReactNode>> = {
  active: <CircleCheck size={14} aria-hidden="true" />,
  inactive: <CircleOff size={14} aria-hidden="true" />,
  marked: <Trash2 size={14} aria-hidden="true" />,
}

/** Заголовок столбца с пояснением ⓘ. */
function Head({ title, help }: { title: string; help: string }) {
  return (
    <th>
      {title} <InfoTip label={title}>{help}</InfoTip>
    </th>
  )
}

/**
 * «Цены» базы 1С: виды цен — розничная, закупочная, любые другие — списком, как справочник «Виды цен» в 1С. Создать, изменить,
 * пометить на удаление (и снять пометку). Цены товаров по виду здесь не показываются: это отбор по виду в «Номенклатуре» (его пока нет). Кнопки записи
 * видны всегда; нет права `prices.write` у базы — они выключены, а причина написана рядом. Экран получает vm и actions (R02).
 */
export function PriceTypesScreen({ vm, actions }: { vm: PriceTypesVm; actions: PriceTypesActions }) {
  const form = vm.form
  const write = vm.writeDisabledReason === null
  return (
    <section className="onec-screen" aria-labelledby="onec-price-types-title">
      <header className="onec-section__head">
        <h2 id="onec-price-types-title">Виды цен</h2>
        <div className="onec-row">
          {(form === null || !form.open) && (
            <>
              <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} disabled={!write} onClick={actions.create}>
                Создать вид цен
              </Button>
              <InfoTip label="Создать вид цен">{PRICE_TYPE_HELP.create}</InfoTip>
            </>
          )}
          <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
            Обновить
          </Button>
        </div>
      </header>
      <p className="onec-muted">
        Здесь — сами виды цен базы 1С: розничная, закупочная и любые другие. Цены конкретных товаров по виду сюда не входят (отбора по виду цен в «Номенклатуре» пока нет).
      </p>
      {vm.writeDisabledReason !== null && (
        <p className="onec-muted" role="note">
          Создание и правка выключены. {vm.writeDisabledReason}
        </p>
      )}
      {vm.done !== null && (
        <p className="onec-muted" role="status">
          {vm.done}
        </p>
      )}
      {form !== null && form.open && (
        <section className="onec-section" aria-labelledby="onec-price-type-form-title">
          <div className="onec-section__head">
            <h3 id="onec-price-type-form-title">{form.draft.key === null ? 'Новый вид цен' : 'Изменить вид цен'}</h3>
            <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={actions.closeForm}>
              Закрыть
            </Button>
          </div>
          <TextField label="Название" value={form.draft.name} onChange={(name) => actions.draftChanged({ ...form.draft, name })} />
          <div className="onec-row">
            <CheckboxField
              label="Цена включает НДС"
              checked={form.draft.includesVat}
              onChange={(includesVat) => actions.draftChanged({ ...form.draft, includesVat })}
            />
            <InfoTip label="Цена включает НДС">{PRICE_TYPE_HELP.vat}</InfoTip>
          </div>
          {form.draft.key !== null && (
            <div className="onec-row">
              <CheckboxField
                label="Недействителен"
                checked={form.draft.inactive}
                onChange={(inactive) => actions.draftChanged({ ...form.draft, inactive })}
              />
              <InfoTip label="Недействителен">{PRICE_TYPE_HELP.inactive}</InfoTip>
            </div>
          )}
          <TextField label="Комментарий" value={form.draft.comment} onChange={(comment) => actions.draftChanged({ ...form.draft, comment })} />
          {form.problem !== null && <p className="onec-muted">{form.problem}</p>}
          {form.error !== null && (
            <StatusMessage
              kind="error"
              title="Вид цен не записан"
              text={form.error.message}
              meta={`Код: ${form.error.code}${form.error.correlationId === null ? '' : ' · запрос ' + form.error.correlationId}`}
            />
          )}
          <div className="onec-row">
            <Button variant="primary" busy={form.saving} disabled={form.problem !== null} onClick={actions.save}>
              {form.draft.key === null ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </section>
      )}
      {vm.markError !== null && (
        <StatusMessage
          kind="error"
          title="Пометка не изменена"
          text={vm.markError.message}
          meta={`Код: ${vm.markError.code}${vm.markError.correlationId === null ? '' : ' · запрос ' + vm.markError.correlationId}`}
        />
      )}
      <QueryState query={vm.query} emptyText="В базе нет ни одного вида цен" onRetry={actions.reload} />
      {vm.rows.length > 0 && (
        <div className="onec-scroll">
          <table className="onec-table">
            <caption className="onec-muted">Видов цен: {vm.rows.length}</caption>
            <thead>
              <tr>
                <th>Название</th>
                <Head title="Как формируются цены" help={PRICE_TYPE_HELP.kind} />
                <Head title="Цена включает НДС" help={PRICE_TYPE_HELP.vat} />
                <Head title="Считается от" help={PRICE_TYPE_HELP.basis} />
                <Head title="Состояние" help={PRICE_TYPE_HELP.state} />
                <Head title="Комментарий" help={PRICE_TYPE_HELP.comment} />
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {vm.rows.map((row) => (
                <tr key={row.key} data-state={row.stateKind}>
                  <td>
                    {row.name}
                    {row.predefined && (
                      <>
                        {' '}
                        <span className="onec-muted">· системный</span> <InfoTip label="Системный вид цен">{PRICE_TYPE_HELP.predefined}</InfoTip>
                      </>
                    )}
                  </td>
                  <td>{row.kind}</td>
                  <td>{row.vat}</td>
                  <td>{row.basis}</td>
                  <td>
                    <span className="onec-row">
                      {STATE_ICONS[row.stateKind]}
                      {row.state}
                    </span>
                  </td>
                  <td>{row.comment}</td>
                  <td>
                    <div className="onec-row">
                      {row.canEdit && (
                        <Button variant="ghost" icon={<Pencil size={16} aria-hidden="true" />} disabled={!write} onClick={() => actions.edit(row.key)}>
                          Изменить
                        </Button>
                      )}
                      {row.canMark && vm.confirmMarkKey !== row.key && (
                        <Button
                          variant="ghost"
                          icon={row.stateKind === 'marked' ? <Undo2 size={16} aria-hidden="true" /> : <Trash2 size={16} aria-hidden="true" />}
                          disabled={!write}
                          onClick={() => actions.askMark(row.key)}
                        >
                          {row.markLabel}
                        </Button>
                      )}
                      {vm.confirmMarkKey === row.key && (
                        <>
                          <span>{row.markLabel}: «{row.name}»?</span>
                          <Button variant="primary" busy={vm.marking} onClick={actions.confirmMark}>
                            Да
                          </Button>
                          <Button variant="ghost" disabled={vm.marking} onClick={actions.cancelMark}>
                            Отмена
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
